import { useMemo } from "react";
import type { ComparisonRow, VarianceDetail } from "../../types/shadow-run";
import { formatNZD, formatPercentage } from "../../utils/formatters";
import { ExpectedBadge } from "../ExpectedBadge/ExpectedBadge";
import styles from "./ComparisonTable.module.css";

export interface ComparisonTableProps {
  rows: ComparisonRow[];
  varianceDetails?: VarianceDetail[];
}

export function ComparisonTable({ rows, varianceDetails = [] }: ComparisonTableProps) {
  const varianceMap = useMemo(() => {
    const map = new Map<string, VarianceDetail>();
    for (const v of varianceDetails) {
      map.set(v.field, v);
    }
    return map;
  }, [varianceDetails]);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Field</th>
            <th className={`${styles.th} ${styles.numCol}`}>FSC Value</th>
            <th className={`${styles.th} ${styles.numCol}`}>Pacific Value</th>
            <th className={`${styles.th} ${styles.numCol}`}>Difference</th>
            <th className={`${styles.th} ${styles.numCol}`}>Percentage</th>
            {varianceDetails.length > 0 && (
              <th className={styles.th}>Status</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const hasVariance = row.difference !== 0;
            const variance = varianceMap.get(row.field);
            const rowClass = hasVariance ? `${styles.row} ${styles.varianceRow}` : styles.row;

            return (
              <tr key={row.field} className={rowClass}>
                <td className={styles.td}>
                  <span className={styles.fieldName}>
                    {row.label ?? row.field}
                  </span>
                </td>
                <td className={`${styles.td} ${styles.numCol}`}>
                  {formatNZD(row.fsc_value)}
                </td>
                <td className={`${styles.td} ${styles.numCol}`}>
                  {formatNZD(row.pacific_value)}
                </td>
                <td className={`${styles.td} ${styles.numCol} ${hasVariance ? styles.diffVariance : styles.diffMatch}`}>
                  {formatNZD(row.difference)}
                </td>
                <td className={`${styles.td} ${styles.numCol} ${hasVariance ? styles.diffVariance : styles.diffMatch}`}>
                  {formatPercentage(row.percentage_difference)}
                </td>
                {varianceDetails.length > 0 && (
                  <td className={styles.td}>
                    {variance && (
                      <ExpectedBadge
                        isExpected={variance.is_expected}
                        reason={variance.expected_reason}
                      />
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
